import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspBackButton from "../../components/common/TspBackButton";
import useTspPermission from "../../hooks/useTspPermission";
import { JOURNEY_STATUS_LABELS } from "../../constants/tspJourneyStatus";
import { JOURNEY_RAIL_SECTIONS } from "../../constants/tspJourneyRail";
import useTspAddJourneyData from "./useTspAddJourneyData";
import AddJourneyActions from "./components/AddJourneyActions";
import JourneySectionNav from "./components/JourneySectionNav";
import JourneyRoutePlanCard from "./components/JourneyRoutePlanCard";
import JourneyPlaceAdder from "./builder/JourneyPlaceAdder";
import JourneyPlaceList from "./builder/JourneyPlaceList";
import JourneyLeaderField from "./builder/JourneyLeaderField";
import useLivePlaceStore from "./builder/useLivePlaceStore";
import useTspJourneyEditor from "./builder/useTspJourneyEditor";
import useJourneyPlacePerms from "./builder/useJourneyPlacePerms";
import useLazyBookCatalog from "./builder/useLazyBookCatalog";
import TspApiService from "../../api/tspApiService";
import { tspErrorMessage } from "../../hooks/useTspApiResource";


// Edit Journey (/tsp/journeys/:journeyId/edit) — the same Journey Builder as Add
// Journey, on an existing journey: its Team Leader, its places as cards (with
// their visit dates), each card's sections with the records already saved —
// every one editable right here. Places and
// section records are saved as they are changed (through the existing APIs);
// "Update Journey" saves the Team Leader and returns to the journey.
export default function TspEditJourneyPage() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editor = useTspJourneyEditor(journeyId, { autoOpenFirst: location.state?.autoOpenFirst ?? true });
  const options = useTspAddJourneyData();
  const { loading: permissionsLoading } = useTspPermission();
  const bookCatalog = useLazyBookCatalog();
  const { journey, data } = editor;
  // What each place's sections may show and record (the server checks again).
  const { perms, views } = useJourneyPlacePerms(data);

  if (journey.error) return <TspLoadError error={journey.error} onRetry={journey.reload} what="this journey" />;
  if (!data || options.loading) return <div className="tsp-page tsp-dashboard-loading">Loading journey…</div>;
  if (options.error) return <TspLoadError error={options.error} onRetry={options.reload} what="this form" />;

  const journeyPath = `/tsp/journeys/${data.id}`;
  const running = data.status === "IN_PROGRESS";
  const capabilities = data.capabilities || {};
  const canEditLeader = Boolean(capabilities.edit) && (data.editable_fields || []).includes("team_leader_name");
  const canEditRoute = Boolean(capabilities.edit_route);
  const range = { startDate: data.start_date || "", endDate: data.end_date || "" };
  const anyChange = running && (canEditLeader || canEditRoute || perms.visits || perms.books || perms.accommodation || perms.media || perms.advice || perms.response);

  const handleCancel = () => {
    if (editor.leaderChanged && !window.confirm("Discard the change to the Team Leader?")) return;
    navigate(journeyPath);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await editor.saveJourney();
    if (!result.ok) {
      if (result.message) notification("error", result.message);
      return;
    }
    notification("success", "Journey updated.");
    navigate(journeyPath);
  };

  // Each card's own state and actions; its sections read and save through the API.
  const cardProps = (place, index) => ({
    expanded: Boolean(editor.openCards[place.key]),
    onToggle: () => editor.toggleCard(place.key),
    activeTab: editor.activeTabs[place.key] || null,
    onTabChange: (tab) => editor.setCardTab(place.key, tab),
    usePlaceStore: (current, load) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks -- always this one hook for the card's whole life
      useLivePlaceStore({ journeyId: data.id, place: current, visitRecord: editor.visitsByLocation.get(current.locationId) || null, load, perms: views, range, onVisitChanged: editor.reloadAll }),
    perms,
    range,
    bookCatalog,
    canEditRoute,
    onDateChange: (date) => editor.setPlaceDate(place, date),
    onStatusChange: async (currentPlace, status) => {
      try {
        const locId = currentPlace.locationId || currentPlace.location_id;
        await TspApiService.updatePlaceStatus(data.id, {
          location_id: Number(locId),
          status: status,
        });
        notification("success", `Status updated to ${status === "live" ? "In Progress" : status === "completed" ? "Completed" : "Upcoming"}.`);
        editor.reloadAll();
      } catch (err) {
        notification("error", tspErrorMessage(err, "Could not update status."));
      }
    },
    onRemove: () =>
      editor.removePlace(place).then((result) => {
        if (!result.ok && result.message) notification("error", result.message);
      }),
    onMove: (direction) =>
      editor.movePlace(index, direction).then((result) => {
        if (!result.ok) notification("error", result.message);
      }),
  });

  return (
    <div className="tsp-page tsp-add-journey-page tsp-journey-builder">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Edit Journey</h1>
          <TspBreadcrumb items={["Dashboard", "Journeys", data.journey_name, "Edit"]} />
        </div>
        <TspBackButton fallback={journeyPath} />
      </div>

      {!running && <p className="tsp-preview-note tsp-preview-note-warn">This journey is {JOURNEY_STATUS_LABELS[data.status] || data.status}: its places and details can be viewed but no longer changed.</p>}

      <form className="tsp-journey-detail-layout" onSubmit={handleSubmit} noValidate aria-busy={editor.saving}>
        <div className="tsp-journey-detail-main">
          <JourneyRoutePlanCard places={editor.places} />
          <JourneyPlaceList places={editor.places} cardProps={cardProps} />
          <JourneyPlaceAdder
            states={options.data.states}
            onAdd={editor.addPlace}
            placeCount={editor.places.length}
            showPicker={canEditRoute}
            leader={<JourneyLeaderField value={editor.leader} error={editor.leaderError || undefined} onChange={editor.setLeader} disabled={editor.saving || !canEditLeader} />}
          />
        </div>

        <JourneySectionNav sections={JOURNEY_RAIL_SECTIONS} currentPage="add" pageRoutes={{ journey: journeyPath, stats: `/tsp/stats/${data.id}` }}>
          <AddJourneyActions canManage={canEditLeader || !editor.leaderChanged} permissionsLoading={permissionsLoading} submitting={editor.saving} onCancel={handleCancel} />
        </JourneySectionNav>
      </form>
      <ToastContainer />
    </div>
  );
}
